/**
 * The subscribe dialog's options (TMMAPP-0116, from the plan step's working parts): the operating
 * currency, the Monthly / Annual plan cards with the served prices, the prepaid-AI note, the card
 * with its promo code, and the served total. Every figure is the quote's; nothing is computed here
 * but the trial-end civil date.
 */
import { MoneyText } from '@/components/MoneyText';
import { KindChip } from '@/components/KindChip';
import { useCallback, useEffect, useId, useRef, type ReactElement, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import type { EntitlementsQuote, PriceBook } from '@/api/entitlements';
import { errorCopy } from '@/api/errorCopy';
import { CardCapture, type CardCaptureHandle } from '@/features/billing/CardCapture';
import { PromoCodeField } from '@/features/billing/PromoCodeField';
import { currencyDisplayName, formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { ReferenceLine } from '@/components/ReferenceLine';
import { resolveTokens } from '@/theme/theme';
import { SUBSCRIBE_CURRENCIES } from './currencies';
import { trialEndCivil } from './planPrices';
import {
  planAmounts,
  planShowsAnnual,
  servedTrialDays,
  type PlanInterval,
  type PlanValues,
  type QuoteStatus,
} from './planQuote';

interface SubscribePlanProps {
  prices: PriceBook;
  timezone: string;
  /** The operating currency the subscribe body sends. Plan prices stay in the price book's. */
  currency: string;
  onCurrencyChange: (currency: string) => void;
  values: PlanValues;
  onChange: (next: PlanValues) => void;
  cardRef: React.RefObject<CardCaptureHandle | null>;
  cardError: string | null;
  promoError: string | null;
  /** Present when the code just sold out (`409 EXHAUSTED`): buy without it. */
  onContinueWithoutPromo?: (() => void) | undefined;
  planError: string | null;
  /** The plan error's support reference, as its own line (BILLING_CONFIGURATION, TMMAPP-0122). */
  planReference?: string | null;
  busy: boolean;
  quote: EntitlementsQuote | null;
  quoteStatus: QuoteStatus;
  /** The failed quote's error (its catalog copy shows) and Retry (TMMAPP-0119 item 15). */
  quoteError?: unknown;
  onRetryQuote?: () => void;
}

function IncludedLine({ seats, connections }: { seats: number; connections: number }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const parts: string[] = [];
  if (seats > 0) parts.push(t('plan.includedVirtualAccounts', { count: seats }));
  if (connections > 0) parts.push(t('plan.includedConnections', { count: connections }));
  if (parts.length === 0) return null;
  const formatted = new Intl.ListFormat(locale, { type: 'conjunction' }).formatToParts(parts);
  return (
    <Typography variant="body2" component="div" sx={{ color: 'inherit' }}>
      {t('plan.includedLead')}{' '}
      {formatted.map((part, index) =>
        part.type === 'element' ? (
          <Box component="strong" key={index} sx={{ fontWeight: 500 }}>
            {part.value}
          </Box>
        ) : (
          <span key={index}>{part.value}</span>
        ),
      )}
      .
    </Typography>
  );
}

function PriceFigure({
  ready,
  minor,
  currency,
  suffix,
}: {
  ready: boolean;
  minor: number | null;
  currency: string | null;
  suffix: string;
}) {
  // A ready quote with no figure is not a price. Never an ellipsis or an em dash.
  if (ready && minor == null) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
      {minor != null ? (
        <MoneyText minor={minor} currency={currency} variant="figureSm" component="span" />
      ) : (
        <Typography variant="figureSm" component="span">
          …
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" component="span">
        {suffix}
      </Typography>
    </Box>
  );
}

/** A served amount. Loading may say it is still coming; a ready gap is empty, never a stand-in price. */
function servedAmount(ready: boolean, minor: number | null, currency: string): ReactElement {
  if (minor != null) return <MoneyText minor={minor} currency={currency} />;
  if (ready) return <span />;
  return <span>…</span>;
}

export function SubscribePlan({
  prices,
  timezone,
  currency: operatingCurrency,
  onCurrencyChange,
  values,
  onChange,
  cardRef,
  cardError,
  promoError,
  onContinueWithoutPromo,
  planError,
  planReference = null,
  busy,
  quote,
  quoteStatus,
  quoteError,
  onRetryQuote,
}: SubscribePlanProps) {
  const { t } = useTranslation();
  const cardTitleId = useId();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const showAnnual = planShowsAnnual(prices, values);
  const days = servedTrialDays(quote, quoteStatus);
  const trialEnd =
    days != null && days > 0 ? formatCivilDate(trialEndCivil(days, timezone), locale) : '';
  const {
    totalMinor,
    currency: quoteCurrency,
    ready,
    annualOmitted,
  } = planAmounts(quote, quoteStatus, showAnnual, values, prices.currency);
  const currency = quoteCurrency || prices.currency;
  const showAnnualCard = showAnnual && !annualOmitted;

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    if (!annualOmitted || values.interval !== 'YEAR') return;
    onChange({ ...valuesRef.current, interval: 'MONTH' });
  }, [annualOmitted, values.interval, onChange]);

  const handleCompleteChange = useCallback(
    (complete: boolean) => {
      if (valuesRef.current.cardComplete === complete) return;
      onChange({ ...valuesRef.current, cardComplete: complete });
    },
    [onChange],
  );

  const monthlyMinor = quote?.baseMinor ?? null;
  const annualMinor = quote?.annual?.baseMinor ?? null;
  const monthlyAfter = quote?.totalMonthlyMinor ?? null;
  const annualAfter = quote?.annual?.totalFirstYearMinor ?? null;
  const savingsBp = prices.annual?.savingsBp ?? null;
  const saveLabel =
    savingsBp != null && savingsBp > 0 ? t('plan.saveChip', { percent: savingsBp / 10000 }) : null;
  const currencyLabelId = 'subscribe-currency-label';

  return (
    <Stack spacing={2}>
      <FormControl fullWidth>
        <InputLabel id={currencyLabelId}>{t('subscribeDialog.currencyLabel')}</InputLabel>
        <Select
          labelId={currencyLabelId}
          id="subscribe-currency"
          label={t('subscribeDialog.currencyLabel')}
          value={operatingCurrency}
          disabled={busy}
          onChange={(event) => onCurrencyChange(event.target.value)}
        >
          {SUBSCRIBE_CURRENCIES.map((code) => (
            <MenuItem key={code} value={code}>
              {/* The code is an identifier: mono, inside the translated CODE · Name order. */}
              <Trans
                t={t}
                i18nKey="subscribeDialog.currencyOption"
                values={{ code, name: currencyDisplayName(code, locale) }}
                components={{ code: <Typography variant="mono" component="span" /> }}
              />
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {t('subscribeDialog.currencyHelp', { currency: operatingCurrency })}
        </FormHelperText>
      </FormControl>

      <RadioGroup
        aria-label={t('plan.intervalLabel')}
        value={showAnnualCard ? values.interval : 'MONTH'}
        onChange={(_event, next: string) => {
          if (next === 'MONTH' || next === 'YEAR') onChange({ ...values, interval: next });
        }}
        sx={{
          display: 'grid',
          // `min(…, 100%)` keeps one card inside a column narrower than the minimum (320px reflow).
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
          gap: 2,
        }}
      >
        <IntervalCard
          value="MONTH"
          selected={!showAnnualCard || values.interval === 'MONTH'}
          label={t('plan.monthly')}
          ready={ready}
          minor={monthlyMinor}
          currency={currency}
          suffix={t('plan.perFirstMonth')}
          after={
            <Trans
              i18nKey="plan.subsequentMonths"
              components={{ amount: servedAmount(ready, monthlyAfter, currency) }}
            />
          }
        />
        {showAnnualCard && (
          <IntervalCard
            value="YEAR"
            selected={values.interval === 'YEAR'}
            label={t('plan.annual')}
            chip={saveLabel}
            ready={ready}
            minor={annualMinor}
            currency={currency}
            suffix={t('plan.perFirstYear')}
            after={
              <Trans
                i18nKey="plan.subsequentYear"
                components={{ amount: servedAmount(ready, annualAfter, currency) }}
              />
            }
          />
        )}
      </RadioGroup>
      {prices.currency !== operatingCurrency && (
        <Typography variant="body2" color="text.secondary">
          {t('plan.billedIn', { currency: prices.currency })}
        </Typography>
      )}
      {quoteStatus === 'error' && (
        <Alert
          severity="error"
          action={
            onRetryQuote !== undefined ? (
              <Button color="inherit" size="small" onClick={onRetryQuote}>
                {t('common.retry')}
              </Button>
            ) : undefined
          }
        >
          {errorCopy(t, quoteError, 'plan.loadError')}
        </Alert>
      )}
      {planError != null && (
        <Alert severity="error" tabIndex={-1} data-subscribe-invalid="">
          {planError}
          {planReference !== null && <ReferenceLine requestId={planReference} ink="inherit" />}
        </Alert>
      )}

      <Card sx={{ p: 2 }} data-plan-ai="">
        {/* AI is an opt-in; it's prepaid (TMMAPI-0151), bought after subscribing — never here. */}
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" component="h3">
              {t('plan.aiOn')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('plan.aiPrepaid', {
                amount: formatMoneyMinor(prices.aiFirstPurchaseMinor, currency, locale),
              })}
            </Typography>
          </Box>
          <Switch
            checked={values.aiOn}
            disabled={busy}
            onChange={(_event, checked) => onChange({ ...values, aiOn: checked })}
            slotProps={{ input: { 'aria-label': t('plan.aiOn') } }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('plan.aiPurpose')}
        </Typography>
      </Card>

      <Card sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="eyebrow" component="h3" id={cardTitleId}>
            {t('plan.cardTitle')}
          </Typography>
          {cardError != null && (
            <Alert severity="error" tabIndex={-1} data-subscribe-invalid="">
              {cardError}
            </Alert>
          )}
          <CardCapture
            ref={cardRef}
            hideSubmit
            showStatementDescriptor
            labelledBy={cardTitleId}
            onCompleteChange={handleCompleteChange}
            busy={busy}
          >
            <PromoCodeField
              applied={values.promo}
              onApplied={(promo) => onChange({ ...values, promo })}
              error={promoError}
              onContinueWithout={onContinueWithoutPromo}
              quotePromotion={quote?.promotion ?? null}
              quoteReady={quoteStatus === 'ready'}
            />
          </CardCapture>
        </Stack>
      </Card>

      <Box
        data-subscribe-total=""
        sx={{
          bgcolor: tokens.brandSoft,
          color: tokens.text,
          borderRadius: `${tokens.radius.card}px`,
          px: 2.25,
          py: 2,
          display: 'grid',
          gap: 1,
        }}
      >
        {days == null ? (
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            {t('common.loading')}
          </Typography>
        ) : days > 0 ? (
          <Typography variant="body2" component="div" sx={{ color: 'inherit' }}>
            <Trans
              i18nKey="plan.trialFree"
              count={days}
              values={{ count: days, trialEnd }}
              components={{
                amount: ready ? (
                  <MoneyText minor={totalMinor} currency={currency} />
                ) : (
                  <span>…</span>
                ),
              }}
            />
          </Typography>
        ) : (
          <Typography variant="body2" component="div" sx={{ color: 'inherit' }}>
            <Trans
              i18nKey="plan.trialToday"
              components={{
                amount: ready ? (
                  <MoneyText minor={totalMinor} currency={currency} />
                ) : (
                  <span>…</span>
                ),
              }}
            />
          </Typography>
        )}
        <IncludedLine
          seats={quote?.seats.includedCount ?? 0}
          connections={quote?.connections.includedCount ?? 0}
        />
        {/* The quote is pre-tax (MM-D-139): Stripe Tax calculates it at checkout. */}
        <Typography variant="body2" component="p" data-tax-at-checkout="" sx={{ color: 'inherit' }}>
          {t('prices.taxAtCheckout')}
        </Typography>
      </Box>
    </Stack>
  );
}

function IntervalCard({
  value,
  selected,
  label,
  chip,
  ready,
  minor,
  currency,
  suffix,
  after,
}: {
  value: PlanInterval;
  selected: boolean;
  label: string;
  chip?: string | null;
  ready: boolean;
  minor: number | null;
  currency: string | null;
  suffix: string;
  after: ReactNode;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Card
      component="label"
      sx={{
        p: 2,
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        borderColor: selected ? tokens.brandBoundary : undefined,
        borderWidth: selected ? 2 : undefined,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Radio
          value={value}
          slotProps={{ input: { 'aria-label': label } }}
          sx={{ marginInlineStart: '-11px' }}
        />
        <Typography component="span" variant="body1">
          {label}
        </Typography>
        {chip != null && <KindChip neutral label={chip} sx={{ marginInlineStart: 1 }} />}
      </Stack>
      <PriceFigure ready={ready} minor={minor} currency={currency} suffix={suffix} />
      <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {after}
      </Typography>
    </Card>
  );
}
